import React, {
  useEffect,
  useState,
  MouseEvent,
  useMemo,
  useCallback,
} from "react";
import CustomPopover from "../common/CustomPopover";
import { useQueryAnswerDocuments } from "./services/useQueryAnswerDocuments";
import { X } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";

const AiSparkleIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      {...props}
    >
      {/* Large central sparkle */}
      <path d="M12 2 Q15 12 22 12 Q15 12 12 22 Q9 12 2 12 Q9 12 12 2 Z" />
      {/* Smaller sparkle (top-left) */}
      <path d="M5 4 Q6 7 9 7 Q6 7 5 10 Q4 7 1 7 Q4 7 5 4 Z" />
      {/* Smaller sparkle (bottom-left) */}
      <path d="M7 16 Q8 18 10 18 Q8 18 7 20 Q6 18 4 18 Q6 18 7 16 Z" />
      <title>This document selection was assisted by AI</title>
    </svg>
  );
};

const DocumentsUsedInQueryAnswer = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [qid, setQid] = useState<string>("");
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );

  const { data, isLoading, isPending } = useQueryAnswerDocuments({ qid });

  if (!isLoading && !isPending && data?.toolId === "web_search") {
    // Web search does not use documents.
    return null;
  }

  const [documents, setDocuments] = useState<
    {
      fileId: string;
      fileName: string;
    }[]
  >([]);

  const [event, setEvent] = useState<MouseEvent | null>(null);
  const [leftId, setLeftId] = useState<string>("");

  const showDocumentsUsedInQueryAnswer = async (
    e: MouseEvent,
    { qid }: { qid: string }
  ) => {
    setAnchorEl(e.target as HTMLElement);
    setEvent(e);
    setQid(qid);
    setLeftId(
      (
        (e.target as HTMLElement).getBoundingClientRect().left +
        Math.random() * 100
      ).toString()
    );
  };

  useEffect(() => {
    if (isLoading || isPending) {
      setDocuments([]);
      return;
    }

    if (!data) return;

    const docList = [];
    if (data.documents) {
      for (const docId in data.documents) {
        const doc = data.documents[docId];
        docList.push({
          fileId: docId,
          fileName: doc.file_name,
        });
      }
    }
    setDocuments(docList);
  }, [data, isLoading, isPending]);

  useEffect(() => {
    (window as Window).showDocumentsUsedInQueryAnswer =
      showDocumentsUsedInQueryAnswer;
  }, [showDocumentsUsedInQueryAnswer]);

  useEffect(() => {
    // The file viewer opening/closing reflows the page, so the popover
    // anchor's rect isn't accurate until the next frame is painted.
    const frame = requestAnimationFrame(() => {
      if (event?.target) {
        setLeftId(
          (
            (event.target as HTMLElement).getBoundingClientRect().left +
            Math.random() * 100
          ).toString()
        );
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [fileViewerData.isOpen]);

  return (
    <>
      <CustomPopover
        id={anchorEl ? "simple-popover-" + leftId : undefined}
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        closeOnClickOutside={false}
        childWidth={800}
      >
        <div className="h-[200px] w-[600px] rounded-md overflow-y-auto border-t-0">
          {isLoading || isPending ? (
            <div className="p-4">Loading...</div>
          ) : (
            <>
              <div className="text-[14px] p-2 border-b bg-gray-100 rounded-t-md mb-2 flex items-center justify-between absolute top-0 left-0 w-full">
                <span className="flex items-center">
                  {data && !data.isManualSelection && (
                    <AiSparkleIcon className="w-4 h-4 text-blue-500 mr-1" />
                  )}
                  Referenced Documents: {documents.length}
                </span>
                <span
                  className="cursor-pointer flex items-center text-gray-500 hover:text-gray-800 transition-all duration-300"
                  onClick={() => setAnchorEl(null)}
                >
                  <X className="w-4" />
                  <span className="ml-1">Close</span>
                </span>
              </div>
              <div className="flex flex-wrap mt-[45px] justify-left pl-1">
                {documents.length === 0 ? (
                  <div>No documents used</div>
                ) : (
                  documents.map((doc, index) => (
                    <div
                      key={doc.fileId}
                      className="text-[12px] w-[48%] p-1 cursor-pointer text-blue-700 text-ellipsis line-clamp-1 break-all"
                      onClick={async () => {
                        // Dispatch event to open file viewer using Layout.tsx fileViewer
                        window.dispatchEvent(
                          new CustomEvent("openDocumentPanel", {
                            detail: {
                              fileId: doc.fileId,
                              fileName: doc.fileName,
                              isDirectFileOpen: true,
                            },
                          })
                        );
                        // Close the popover when a document is clicked
                        setAnchorEl(null);
                      }}
                    >
                      {index + 1}. {doc.fileName}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </CustomPopover>
    </>
  );
};

export default DocumentsUsedInQueryAnswer;

interface DocumentsUsedInAnswerListProps {
  qid: string;
  hide?: boolean;
  close?: () => void;
}

export const DocumentsUsedInAnswerList = ({
  qid,
  hide = false,
  close,
}: DocumentsUsedInAnswerListProps) => {
  if (hide) return null;

  const { data, isLoading, isPending } = useQueryAnswerDocuments({ qid });
  const [collapsedVal, setCollapsed] = useState<null | boolean>(null);

  if (!isLoading && !isPending && data?.toolId === "web_search") {
    return null;
  }
  const documents = useMemo(() => {
    if (!data) return [];

    const docList = [];
    if (data.documents) {
      for (const docId in data.documents) {
        const doc = data.documents[docId];
        docList.push({
          fileId: docId,
          fileName: doc.file_name,
        });
      }
    }
    return docList;
  }, [data]);

  const collapsed =
    collapsedVal !== null ? collapsedVal : documents.length > 50;
  const toggleCollapsed = useCallback(
    () => setCollapsed(!collapsed),
    [collapsed]
  );

  if (isLoading || isPending)
    return (
      <>
        <h1>
          <b>Referenced Documents</b>
        </h1>
        <div className="p-4">Loading...</div>
      </>
    );
  if (documents.length === 0)
    return (
      <>
        <h1>
          <b>Referenced Documents</b>
        </h1>
        <div className="flex flex-wrap justify-left pl-1">
          No documents used
        </div>
      </>
    );

  const CollapsedIcon = collapsed ? ChevronUp : ChevronDown;

  return (
    <>
      <h1>
        <b
          className="flex gap-1 items-center cursor-pointer select-none"
          onClick={toggleCollapsed}
        >
          <CollapsedIcon className="w-4 h-4" />
          Referenced Documents ({documents.length})
          {data && !data.isManualSelection && (
            <AiSparkleIcon className="w-4 h-4 text-blue-500 mr-1" />
          )}
        </b>
      </h1>
      <div className="flex flex-wrap justify-left pl-1">
        {(collapsed ? [] : documents).map((doc, index) => (
          <div
            key={doc.fileId}
            className="text-[12px] w-[48%] p-1 cursor-pointer text-blue-700 text-ellipsis line-clamp-1 break-all"
            onClick={() => {
              // Dispatch event to open file viewer using Layout.tsx fileViewer
              window.dispatchEvent(
                new CustomEvent("openDocumentPanel", {
                  detail: {
                    fileId: doc.fileId,
                    fileName: doc.fileName,
                    isDirectFileOpen: true,
                  },
                })
              );
              // Close the popover when a document is clicked
              close?.();
            }}
          >
            {index + 1}. {doc.fileName}
          </div>
        ))}
      </div>
    </>
  );
};
